import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Modal, Form, Button, Alert, OverlayTrigger, Tooltip, Row, Col, InputGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import styles from './CreateUserModal.module.scss';

/**
 * 👥 Modal Unificado de Criação/Edição de Usuários
 * 
 * Suporta 3 tipos:
 * - Colaborador: Com grupo de permissões
 * - Vendedor: Exclusivo do produtor
 * - Afiliado Direto: Com produtos vinculados
 */
const CreateUserModal = ({ 
  show, 
  onHide, 
  onSave, 
  editingUser, 
  permissionGroups = [],
  products = []
}) => {
  // Estados
  const [userType, setUserType] = useState('collaborator'); // 'collaborator', 'seller', 'affiliate'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    permission_group_id: null,
    selectedProducts: [] // Para afiliados
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Estados para busca e paginação de produtos
  const [productSearch, setProductSearch] = useState('');
  const [currentProductPage, setCurrentProductPage] = useState(1);
  const productsPerPage = 6;

  // Tipos de usuário disponíveis
  const userTypes = [
    { 
      value: 'collaborator', 
      label: 'Colaborador', 
      icon: 'mdi:account-tie',
      description: 'Membro da equipe com grupo de permissões'
    },
    { 
      value: 'seller', 
      label: 'Vendedor', 
      icon: 'mdi:account-cash',
      description: 'Vendedor exclusivo vinculado a seus produtos'
    },
    { 
      value: 'affiliate', 
      label: 'Afiliado Direto', 
      icon: 'mdi:account-network',
      description: 'Afiliado convidado por você diretamente'
    }
  ];

  // Resetar formulário quando modal abre/fecha
  useEffect(() => {
    if (show) {
      if (editingUser) {
        // Edição - preencher dados
        setFormData({
          name: editingUser.name || '',
          email: editingUser.email || '',
          phone: editingUser.phone || '',
          password: '',
          permission_group_id: editingUser.permission_group_id || null,
          selectedProducts: editingUser.affiliated_products?.map(p => p.product_id) || []
        });
        // Determinar tipo baseado no usuário
        if (editingUser.seller_exclusive_owner_id) {
          setUserType('seller');
        } else if (editingUser.type === 'direct_affiliate') {
          setUserType('affiliate');
        } else {
          setUserType('collaborator');
        }
      } else {
        // Criação - limpar
        setFormData({
          name: '',
          email: '',
          phone: '',
          password: '',
          permission_group_id: null,
          selectedProducts: []
        });
        setUserType('collaborator');
      }
      setError('');
      // Resetar busca e paginação
      setProductSearch('');
      setCurrentProductPage(1);
    }
  }, [show, editingUser]);

  // Validação
  const validate = () => {
    if (!formData.name || formData.name.trim().length < 3) {
      setError('Nome deve ter pelo menos 3 caracteres');
      return false;
    }
    if (!formData.email || !formData.email.includes('@')) {
      setError('Email inválido');
      return false;
    }
    if (!editingUser && (!formData.password || formData.password.length < 8)) {
      setError('Senha deve ter pelo menos 8 caracteres');
      return false;
    }
    // Validar requisitos da senha (se não foi gerada automaticamente)
    if (!editingUser && formData.password) {
      const hasLowercase = /[a-z]/.test(formData.password);
      const hasUppercase = /[A-Z]/.test(formData.password);
      const hasNumber = /[0-9]/.test(formData.password);
      const hasSpecial = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(formData.password);
      
      if (!hasLowercase || !hasUppercase || !hasNumber || !hasSpecial) {
        setError('Senha deve conter: letra minúscula, maiúscula, número e caractere especial');
        return false;
      }
    }
    if (userType === 'collaborator' && !formData.permission_group_id) {
      setError('Selecione um grupo de permissões');
      return false;
    }
    if (userType === 'affiliate' && formData.selectedProducts.length === 0) {
      setError('Selecione pelo menos um produto para o afiliado');
      return false;
    }
    return true;
  };

  // Salvar
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) return;

    setLoading(true);
    try {
      await onSave({
        ...formData,
        userType
      });
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar usuário');
    } finally {
      setLoading(false);
    }
  };

  // Fechar
  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      permission_group_id: null,
      selectedProducts: []
    });
    setUserType('collaborator');
    setError('');
    onHide();
  };

  // Toggle grupo de permissões
  const handleToggleGroup = (groupId) => {
    setFormData(prev => ({
      ...prev,
      permission_group_id: prev.permission_group_id === groupId ? null : groupId
    }));
  };

  // Toggle produto (para afiliados)
  const handleToggleProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      selectedProducts: prev.selectedProducts.includes(productId)
        ? prev.selectedProducts.filter(id => id !== productId)
        : [...prev.selectedProducts, productId]
    }));
  };

  // Gerar senha aleatória
  const generateRandomPassword = () => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const allChars = lowercase + uppercase + numbers + special;
    
    // Garantir pelo menos um de cada tipo
    let password = '';
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Completar até 12 caracteres (mínimo 8, mas vamos fazer 12)
    for (let i = password.length; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Embaralhar
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setFormData(prev => ({ ...prev, password }));
    toast.success('Senha gerada com sucesso!', { autoClose: 2000 });
  };

  // Filtrar produtos por busca
  const filteredProducts = products.filter(product => 
    product.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
    product.product_code?.toLowerCase().includes(productSearch.toLowerCase()) ||
    product.description?.toLowerCase().includes(productSearch.toLowerCase())
  );

  // Paginação de produtos
  const totalProductPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startProductIndex = (currentProductPage - 1) * productsPerPage;
  const endProductIndex = startProductIndex + productsPerPage;
  const paginatedProducts = filteredProducts.slice(startProductIndex, endProductIndex);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      className={styles.createUserModal}
      backdrop="static"
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          <Icon 
            icon={editingUser ? "mdi:pencil" : "mdi:account-plus"} 
            className={styles.titleIcon} 
          />
          {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className={styles.modalBody}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <Icon icon="mdi:alert-circle" /> {error}
            </Alert>
          )}

          <p className={styles.modalSubtitle}>
            {editingUser 
              ? 'Atualize as informações do usuário da sua equipe.'
              : 'Adicione um novo membro à sua equipe escolhendo o tipo adequado.'
            }
          </p>

          {/* Seleção de Tipo de Usuário (apenas criação) */}
          {!editingUser && (
            <Form.Group className="mb-4">
              <Form.Label className={styles.label}>
                <Icon icon="mdi:account-group" /> Tipo de Usuário *
              </Form.Label>
              <div className={styles.userTypeGrid}>
                {userTypes.map(type => (
                  <div
                    key={type.value}
                    className={`${styles.typeCard} ${
                      userType === type.value ? styles.active : ''
                    }`}
                    onClick={() => setUserType(type.value)}
                  >
                    <Icon icon={type.icon} className={styles.typeIcon} />
                    <span className={styles.typeName}>{type.label}</span>
                    <small className={styles.typeDescription}>{type.description}</small>
                  </div>
                ))}
              </div>
            </Form.Group>
          )}

          {/* Dados Básicos */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className={styles.label}>
                  <Icon icon="mdi:account" /> Nome Completo *
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: João da Silva"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className={styles.input}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className={styles.label}>
                  <Icon icon="mdi:email" /> Email *
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={editingUser} // Não permitir editar email
                  className={styles.input}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label className={styles.label}>
                  <Icon icon="mdi:phone" /> Telefone
                </Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="(00) 00000-0000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={styles.input}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className={styles.label}>
                  <Icon icon="mdi:lock" /> Senha {editingUser && '(deixe em branco para manter)'}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder={editingUser ? "••••••••" : "Mínimo 8 caracteres"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    className={styles.input}
                  />
                  {!editingUser && (
                    <Button
                      variant="outline-secondary"
                      onClick={generateRandomPassword}
                      title="Gerar senha aleatória segura"
                      className={styles.generatePasswordButton}
                    >
                      <Icon icon="mdi:dice-6" />
                    </Button>
                  )}
                </InputGroup>
                {!editingUser && (
                  <small className="text-muted d-block mt-1">
                    <Icon icon="mdi:information" className="me-1" />
                    Letra minúscula, maiúscula, número e caractere especial
                  </small>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Campos específicos por tipo */}
          {userType === 'collaborator' && (
            <Form.Group className="mb-4">
              <Form.Label className={styles.label}>
                <Icon icon="mdi:shield-account" /> Grupo de Permissões *
              </Form.Label>
              <p className={styles.helperText}>
                Selecione o grupo que define as permissões deste colaborador
              </p>
              <div className={styles.groupsGrid}>
                {permissionGroups.length === 0 ? (
                  <Alert variant="info" className="mb-0">
                    <Icon icon="mdi:information" /> Nenhum grupo criado ainda. 
                    Crie grupos em "Grupos de Permissões" primeiro.
                  </Alert>
                ) : (
                  permissionGroups.map(group => (
                    <OverlayTrigger
                      key={group.id}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-group-${group.id}`}>
                          {group.description || 'Sem descrição'}
                        </Tooltip>
                      }
                    >
                      <div
                        className={`${styles.groupCard} ${
                          formData.permission_group_id === group.id ? styles.active : ''
                        }`}
                        onClick={() => handleToggleGroup(group.id)}
                      >
                        <Icon icon="mdi:account-group" className={styles.groupIcon} />
                        <span className={styles.groupName}>{group.name}</span>
                        <Icon 
                          icon="mdi:information" 
                          className={styles.infoIcon}
                        />
                      </div>
                    </OverlayTrigger>
                  ))
                )}
              </div>
            </Form.Group>
          )}

          {userType === 'affiliate' && (
            <Form.Group className="mb-4">
              <div className={styles.labelRow}>
                <div>
                  <Form.Label className={styles.label} style={{ marginBottom: 0 }}>
                    <Icon icon="mdi:package-variant" /> Produtos Vinculados *
                  </Form.Label>
                  <p className={styles.helperText} style={{ marginTop: '4px', marginBottom: 0 }}>
                    Selecione os produtos que este afiliado poderá promover
                  </p>
                </div>
                <Form.Control
                  type="text"
                  placeholder="🔍 Buscar..."
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setCurrentProductPage(1); // Resetar página ao buscar
                  }}
                  className={styles.searchInput}
                  size="sm"
                />
              </div>

              {/* Grid de produtos */}
              <div className={styles.productsGrid}>
                {paginatedProducts.length === 0 ? (
                  <Alert variant="info" className="mb-0">
                    <Icon icon="mdi:information" /> 
                    {products.length === 0 
                      ? 'Nenhum produto cadastrado ainda.'
                      : 'Nenhum produto encontrado com essa busca.'
                    }
                  </Alert>
                ) : (
                  paginatedProducts.map(product => (
                    <div
                      key={product.id}
                      className={`${styles.productCard} ${
                        formData.selectedProducts.includes(product.id) ? styles.active : ''
                      }`}
                      onClick={() => handleToggleProduct(product.id)}
                    >
                      {product.image ? (
                        <img src={product.image} alt={product.name} className={styles.productImage} />
                      ) : (
                        <div className={styles.productImagePlaceholder}>
                          <Icon icon="mdi:image" />
                        </div>
                      )}
                      <span className={styles.productName}>{product.name}</span>
                      {product.product_code && (
                        <small className={styles.productCode}>{product.product_code}</small>
                      )}
                      {formData.selectedProducts.includes(product.id) && (
                        <Icon icon="mdi:check-circle" className={styles.checkIcon} />
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Paginação */}
              {filteredProducts.length > productsPerPage && (
                <div className={styles.paginationContainer}>
                  <div className={styles.paginationInfo}>
                    <Icon icon="mdi:information" className="me-1" />
                    Mostrando {startProductIndex + 1} - {Math.min(endProductIndex, filteredProducts.length)} de {filteredProducts.length} produtos
                    {productSearch && ` (${formData.selectedProducts.length} selecionados)`}
                  </div>
                  <div className={styles.paginationButtons}>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setCurrentProductPage(prev => Math.max(1, prev - 1))}
                      disabled={currentProductPage === 1}
                    >
                      <Icon icon="mdi:chevron-left" />
                      Anterior
                    </Button>
                    <span className={styles.pageNumber}>
                      Página {currentProductPage} de {totalProductPages}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setCurrentProductPage(prev => Math.min(totalProductPages, prev + 1))}
                      disabled={currentProductPage === totalProductPages}
                    >
                      Próxima
                      <Icon icon="mdi:chevron-right" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Contador de selecionados */}
              {formData.selectedProducts.length > 0 && (
                <Alert variant="success" className="mt-3 mb-0">
                  <Icon icon="mdi:check-circle" className="me-2" />
                  <strong>{formData.selectedProducts.length}</strong> produto(s) selecionado(s)
                </Alert>
              )}
            </Form.Group>
          )}

          {userType === 'seller' && (
            <Alert variant="info" className="mb-0">
              <Icon icon="mdi:information" /> 
              <strong>Vendedor Exclusivo:</strong> Será automaticamente vinculado a todos os seus produtos com status pendente de aprovação.
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          <Button 
            variant="secondary" 
            onClick={handleClose}
            disabled={loading}
            className={styles.cancelButton}
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading}
            className={styles.submitButton}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Salvando...
              </>
            ) : (
              <>
                <Icon icon={editingUser ? "mdi:content-save" : "mdi:plus"} className="me-1" />
                {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;

